// ===========================================
// Meet & Greet Funnel Chart Component
// ===========================================
// Multi-line chart showing conversion funnel from bookings to enrollment

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { MeetGreetConversion } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface MeetGreetFunnelChartProps {
  data: MeetGreetConversion[];
  loading?: boolean;
}

// ===========================================
// HELPERS
// ===========================================

// Format month label (e.g., "2026-03" -> "Mar 2026")
const formatMonthLabel = (month: string): string => {
  const [year, monthNum] = month.split('-');
  const date = new Date(parseInt(year), parseInt(monthNum) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Custom tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {formatMonthLabel(label)}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography key={index} variant="caption" display="block" sx={{ color: entry.color }}>
            {entry.name}: {entry.value}
            {entry.name.includes('Rate') ? '%' : ''}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

// ===========================================
// COMPONENT
// ===========================================

export default function MeetGreetFunnelChart({ data, loading }: MeetGreetFunnelChartProps) {
  const [viewMode, setViewMode] = useState<'counts' | 'rates'>('counts');

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Meet & Greet Conversion Funnel
          </Typography>
          <Alert severity="info">
            No meet & greet conversion data available.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Meet & Greet Conversion Funnel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track booking to enrollment conversion
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="counts">Counts</ToggleButton>
            <ToggleButton value="rates">Rates</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonthLabel}
              tick={{ fontSize: 12 }}
            />

            {viewMode === 'counts' ? (
              <>
                <YAxis
                  yAxisId="left"
                  label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalBookings"
                  stroke="#4580E4"
                  strokeWidth={2}
                  name="Total Bookings"
                  dot={{ fill: '#4580E4', r: 4 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="attendedCount"
                  stroke="#96DAC9"
                  strokeWidth={2}
                  name="Attended"
                  dot={{ fill: '#96DAC9', r: 4 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="convertedCount"
                  stroke="#5cb399"
                  strokeWidth={2}
                  name="Converted"
                  dot={{ fill: '#5cb399', r: 4 }}
                />
              </>
            ) : (
              <>
                <YAxis
                  yAxisId="left"
                  label={{ value: 'Rate (%)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="attendanceRate"
                  stroke="#FFCE00"
                  strokeWidth={2}
                  name="Attendance Rate"
                  dot={{ fill: '#FFCE00', r: 4 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="conversionRate"
                  stroke="#5cb399"
                  strokeWidth={2}
                  name="Conversion Rate"
                  dot={{ fill: '#5cb399', r: 4 }}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* Summary Statistics */}
        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Total Bookings
            </Typography>
            <Typography variant="h6">
              {data.reduce((sum, d) => sum + d.totalBookings, 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Avg Attendance Rate
            </Typography>
            <Typography variant="h6">
              {(data.reduce((sum, d) => sum + d.attendanceRate, 0) / data.length).toFixed(1)}%
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Avg Conversion Rate
            </Typography>
            <Typography variant="h6">
              {(data.reduce((sum, d) => sum + d.conversionRate, 0) / data.length).toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
